import { FlexRow, getFileNameFromPath, isValidImage, StyledButton, useLoading } from '@gorila-shared-ui/components';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'baseui/modal';
import { memo } from 'react';
import { createPortal } from 'react-dom';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useFeedback } from '../../hooks/useFeedback';
import { useStyles } from '../../hooks/useStyles';
import { getS3DownloadUrlByKey } from '../../services/appService';
import { downloadBlob } from '../../utils/files';

type Props = {
  s3Key: string;
  source: string;
  onClose: () => void;
  isOpen: boolean;
  header?: React.ReactNode;
  disableDownload?: boolean;
};

const ImageFullScreenPreviewModal = ({ s3Key, source, onClose, isOpen, header, disableDownload }: Readonly<Props>) => {
  const { css, theme, classes, concatenateClasses } = useStyles();
  const { startLoading, stopLoading, loading } = useLoading(false);
  const { showFailFeedback } = useFeedback();
  const mediaStyles = css({
    height: '100%',
    maxWidth: '100%',
    objectFit: 'contain',
  });
  const modalBodyStyles = css({
    flex: 1,
  });
  const modalFooterStyles = css({
    marginLeft: '0 !important',
    marginRight: '0 !important',
    marginTop: '0 !important',
    paddingLeft: theme.sizing.scale800,
    paddingRight: theme.sizing.scale800,
    borderTop: `${theme.borders.border200.borderWidth} ${theme.borders.border200.borderStyle} ${theme.borders.border200.borderColor}`,
  });

  const download = async () => {
    if (!s3Key) return;
    startLoading();
    const { file, error } = await getS3DownloadUrlByKey(s3Key);
    if (error) {
      showFailFeedback(error);
    } else if (file) {
      downloadBlob(file, getFileNameFromPath(s3Key));
    }
    stopLoading();
  };

  return createPortal(
    <Modal
      animate
      closeable
      onClose={onClose}
      isOpen={isOpen}
      size="full"
      overrides={{
        Dialog: {
          style: {
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      {header && <ModalHeader>{header}</ModalHeader>}
      <ModalBody className={modalBodyStyles}>
        <AutoSizer>
          {({ width, height }) => (
            <FlexRow
              centered
              classNames={css({
                width: `${width}px`,
                height: `${height}px`,
              })}
            >
              {isValidImage(source) && (
                <img
                  src={source}
                  alt=""
                  className={mediaStyles}
                />
              )}
            </FlexRow>
          )}
        </AutoSizer>
      </ModalBody>
      <ModalFooter className={modalFooterStyles}>
        <FlexRow
          gap={theme.sizing.scale300}
          classNames={concatenateClasses(classes.centeredEnd, css({ maxWidth: '500px', margin: '0 auto' }))}
        >
          <StyledButton
            kind="secondary"
            onClick={onClose}
            full
          >
            Cerrar
          </StyledButton>
          {!disableDownload && (
            <StyledButton
              onClick={download}
              isLoading={loading}
              full
            >
              Descargar foto
            </StyledButton>
          )}
        </FlexRow>
      </ModalFooter>
    </Modal>,
    document.body,
  );
};

export default memo(ImageFullScreenPreviewModal);
